import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  Mail, Send, Sparkles, Users, FileText, BarChart3, Plus,
  Eye, Clock, CheckCircle, AlertCircle, Loader2, Wand2,
  ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = "https://api.recoverymatch.com";

const TONE_OPTIONS = [
  { value: 'warm', label: 'Warm & Encouraging' },
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual & Friendly' },
  { value: 'urgent', label: 'Urgent / FOMO' },
  { value: 'celebratory', label: 'Celebratory' },
];

const TEMPLATE_TYPES = [
  { value: 'general', label: 'General Update' },
  { value: 'welcome', label: 'Welcome / Onboarding' },
  { value: 're-engagement', label: 'Re-engagement' },
  { value: 'milestone', label: 'Milestone Celebration' },
  { value: 'upgrade', label: 'Upgrade / Upsell' },
  { value: 'announcement', label: 'Feature Announcement' },
  { value: 'newsletter', label: 'Newsletter' },
];

const AdminEmailMarketing = () => {
  const [segments, setSegments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('compose');

  // Compose form state
  const [composing, setComposing] = useState(false);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);

  const [form, setForm] = useState({
    name: '',
    subject: '',
    htmlContent: '',
    segment: 'all',
    aiPrompt: '',
    aiTone: 'warm',
    aiTemplateType: 'general',
  });

  const getToken = () => localStorage.getItem('recoverymatch_admin_token') || localStorage.getItem('token');

  const fetchAll = useCallback(async () => {
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [segRes, campRes, tplRes, analRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/email/segments`, { headers }),
        fetch(`${API_URL}/api/admin/email/campaigns`, { headers }),
        fetch(`${API_URL}/api/admin/email/templates`, { headers }),
        fetch(`${API_URL}/api/admin/email/analytics`, { headers }),
      ]);
      if (segRes.ok) {
        const d = await segRes.json();
        setSegments(d.segments || []);
      }
      if (campRes.ok) {
        const d = await campRes.json();
        setCampaigns(d.campaigns || []);
      }
      if (tplRes.ok) {
        const d = await tplRes.json();
        setTemplates(d.templates || []);
      }
      if (analRes.ok) {
        const d = await analRes.json();
        setAnalytics(d);
      }
    } catch (err) {
      console.error('Failed to fetch email data:', err);
      toast.error('Failed to load email marketing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAIDraft = async () => {
    if (!form.aiPrompt.trim()) {
      toast.error('Please enter a prompt for the AI');
      return;
    }
    setAiDrafting(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/email/ai-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          prompt: form.aiPrompt,
          tone: form.aiTone,
          template_type: form.aiTemplateType,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'AI draft failed');
      }
      const data = await res.json();
      setForm(prev => ({
        ...prev,
        subject: data.subject || prev.subject,
        htmlContent: data.html_content || '',
      }));
      toast.success('AI draft generated! Review and edit below.');
    } catch (err) {
      toast.error(err.message || 'AI drafting failed');
    } finally {
      setAiDrafting(false);
    }
  };

  const handleSaveCampaign = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.htmlContent.trim()) {
      toast.error('Please fill in campaign name, subject, and content');
      return;
    }
    setComposing(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/email/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name,
          subject: form.subject,
          html_content: form.htmlContent,
          segment: form.segment,
        }),
      });
      if (!res.ok) throw new Error('Failed to save campaign');
      const data = await res.json();
      toast.success(`Campaign "${form.name}" saved as draft!`);
      setForm({ name: '', subject: '', htmlContent: '', segment: 'all', aiPrompt: '', aiTone: 'warm', aiTemplateType: 'general' });
      fetchAll();
      return data.campaign_id;
    } catch (err) {
      toast.error(err.message);
      return null;
    } finally {
      setComposing(false);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to send this campaign? This cannot be undone.')) return;
    setSending(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/email/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Send failed');
      }
      const data = await res.json();
      if (!data.has_resend_key) {
        toast.info(`Campaign logged (dev mode) - ${data.sent_count} recipients. Add RESEND_API_KEY for live delivery.`);
      } else {
        toast.success(`Campaign sent to ${data.sent_count} recipients! (${data.failed_count} failed)`);
      }
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleSaveAndSend = async () => {
    const campaignId = await handleSaveCampaign();
    if (campaignId) {
      await handleSendCampaign(campaignId);
    }
  };

  const loadTemplate = (template) => {
    setForm(prev => ({
      ...prev,
      name: template.name,
      subject: template.subject,
      htmlContent: template.html_content,
    }));
    setActiveView('compose');
    toast.success(`Template "${template.name}" loaded into composer`);
  };

  const selectedSegment = segments.find(s => s.id === form.segment);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="email-marketing-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#1C3A7A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-email-marketing">
      {/* Sub-nav */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'compose', label: 'Compose', icon: Mail },
          { id: 'campaigns', label: 'Campaigns', icon: Send },
          { id: 'templates', label: 'Templates', icon: FileText },
          { id: 'analytics', label: 'Analytics', icon: BarChart3 },
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeView === tab.id ? 'default' : 'ghost'}
            onClick={() => setActiveView(tab.id)}
            className="gap-2"
            data-testid={`email-tab-${tab.id}`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* ============ COMPOSE VIEW ============ */}
      {activeView === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: AI Assistant */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-5 w-5 text-[#FAB710]" />
                AI Email Writer
              </CardTitle>
              <CardDescription>Describe what you want and GPT will draft it</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>What's the email about?</Label>
                <Textarea
                  placeholder="e.g., Welcome back inactive users with a special 50% off offer on Recovery+ subscription..."
                  value={form.aiPrompt}
                  onChange={(e) => setForm(prev => ({ ...prev, aiPrompt: e.target.value }))}
                  rows={4}
                  data-testid="ai-prompt-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Tone</Label>
                  <Select value={form.aiTone} onValueChange={(v) => setForm(prev => ({ ...prev, aiTone: v }))}>
                    <SelectTrigger data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TONE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={form.aiTemplateType} onValueChange={(v) => setForm(prev => ({ ...prev, aiTemplateType: v }))}>
                    <SelectTrigger data-testid="ai-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={handleAIDraft}
                disabled={aiDrafting || !form.aiPrompt.trim()}
                className="w-full bg-gradient-to-r from-[#1C3A7A] to-[#2a4f9e] hover:from-[#15306a] hover:to-[#1C3A7A]"
                data-testid="ai-draft-btn"
              >
                {aiDrafting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Drafting...</>
                ) : (
                  <><Wand2 className="h-4 w-4 mr-2" /> Generate Draft</>
                )}
              </Button>

              {/* Segment Picker */}
              <div className="pt-4 border-t space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" /> Target Audience
                </Label>
                <Select value={form.segment} onValueChange={(v) => setForm(prev => ({ ...prev, segment: v }))}>
                  <SelectTrigger data-testid="segment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {segments.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.label} ({s.count.toLocaleString()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedSegment && (
                  <p className="text-xs text-muted-foreground">
                    Will reach <strong>{selectedSegment.count.toLocaleString()}</strong> users
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right: Email Composer */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-5 w-5" />
                Email Composer
              </CardTitle>
              <CardDescription>Edit and preview your email before sending</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Campaign Name</Label>
                  <Input
                    placeholder="e.g., June Re-engagement"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="campaign-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Subject Line</Label>
                  <Input
                    placeholder="e.g., We miss you at RecoveryMatch!"
                    value={form.subject}
                    onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
                    data-testid="campaign-subject-input"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label>Email HTML Content</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                    className="gap-1 text-xs"
                    data-testid="toggle-preview-btn"
                  >
                    <Eye className="h-3 w-3" />
                    {showPreview ? 'Edit' : 'Preview'}
                  </Button>
                </div>
                {showPreview ? (
                  <div
                    className="border rounded-lg p-4 min-h-[300px] max-h-[500px] overflow-y-auto bg-white"
                    dangerouslySetInnerHTML={{ __html: form.htmlContent }}
                    data-testid="email-preview"
                  />
                ) : (
                  <Textarea
                    placeholder="Paste or edit your email HTML here... AI-generated content will appear here."
                    value={form.htmlContent}
                    onChange={(e) => setForm(prev => ({ ...prev, htmlContent: e.target.value }))}
                    rows={12}
                    className="font-mono text-sm"
                    data-testid="email-html-input"
                  />
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleSaveCampaign}
                  disabled={composing || !form.name.trim() || !form.subject.trim()}
                  variant="outline"
                  className="gap-2"
                  data-testid="save-draft-btn"
                >
                  {composing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  Save as Draft
                </Button>
                <Button
                  onClick={handleSaveAndSend}
                  disabled={composing || sending || !form.name.trim() || !form.subject.trim() || !form.htmlContent.trim()}
                  className="gap-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800"
                  data-testid="send-campaign-btn"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Save & Send Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ CAMPAIGNS VIEW ============ */}
      {activeView === 'campaigns' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Email Campaigns
              </CardTitle>
              <CardDescription>{campaigns.length} total campaigns</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAll} className="gap-2">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Mail className="h-12 w-12 mx-auto mb-4 opacity-40" />
                <p className="font-medium">No campaigns yet</p>
                <p className="text-sm mt-1">Create your first email campaign using the Compose tab</p>
                <Button onClick={() => setActiveView('compose')} className="mt-4 gap-2">
                  <Plus className="h-4 w-4" /> Create Campaign
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map((camp) => (
                  <div key={camp.campaign_id} className="border rounded-lg overflow-hidden" data-testid={`campaign-item-${camp.campaign_id}`}>
                    <div
                      className="flex items-center justify-between p-4 hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => setExpandedCampaign(expandedCampaign === camp.campaign_id ? null : camp.campaign_id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${camp.status === 'sent' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                        <div>
                          <p className="font-medium">{camp.name}</p>
                          <p className="text-sm text-muted-foreground">{camp.subject}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant={camp.status === 'sent' ? 'default' : 'secondary'}>
                          {camp.status === 'sent' ? (
                            <><CheckCircle className="h-3 w-3 mr-1" /> Sent</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> Draft</>
                          )}
                        </Badge>
                        <div className="text-right text-sm text-muted-foreground">
                          <p>{camp.segment?.replace(/_/g, ' ')}</p>
                          <p className="text-xs">{new Date(camp.created_at).toLocaleDateString()}</p>
                        </div>
                        {expandedCampaign === camp.campaign_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                    </div>
                    {expandedCampaign === camp.campaign_id && (
                      <div className="border-t p-4 bg-muted/20">
                        <div className="grid grid-cols-4 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Recipients</p>
                            <p className="text-lg font-bold">{camp.total_recipients || camp.sent_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Sent</p>
                            <p className="text-lg font-bold">{camp.sent_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Opens</p>
                            <p className="text-lg font-bold">{camp.open_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Clicks</p>
                            <p className="text-lg font-bold">{camp.click_count || 0}</p>
                          </div>
                        </div>
                        {camp.status === 'draft' && (
                          <Button
                            onClick={() => handleSendCampaign(camp.campaign_id)}
                            disabled={sending}
                            size="sm"
                            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`send-btn-${camp.campaign_id}`}
                          >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            Send Now
                          </Button>
                        )}
                        {camp.sent_at && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Sent on {new Date(camp.sent_at).toLocaleString()}
                            {camp.failed_count > 0 && <span className="text-red-500 ml-2">({camp.failed_count} failed)</span>}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ TEMPLATES VIEW ============ */}
      {activeView === 'templates' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Email Templates
            </CardTitle>
            <CardDescription>Pre-built templates — click to load into the composer</CardDescription>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No templates available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((tpl) => (
                  <div
                    key={tpl.template_id}
                    className="border rounded-lg p-4 hover:border-[#1C3A7A]/50 hover:bg-muted/20 cursor-pointer transition-all"
                    onClick={() => loadTemplate(tpl)}
                    data-testid={`template-${tpl.template_id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{tpl.name}</h4>
                      <Badge variant="outline" className="text-xs">{tpl.category}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{tpl.subject}</p>
                    <Button variant="ghost" size="sm" className="gap-1 text-xs">
                      <Mail className="h-3 w-3" /> Use Template
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ ANALYTICS VIEW ============ */}
      {activeView === 'analytics' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Campaigns</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{analytics?.stats?.total_campaigns || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Emails Sent</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{(analytics?.stats?.total_sent || 0).toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Opens</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{(analytics?.stats?.total_opens || 0).toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Clicks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{(analytics?.stats?.total_clicks || 0).toLocaleString()}</div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Campaigns */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Recent Campaign Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!analytics?.recent_campaigns || analytics.recent_campaigns.length === 0) ? (
                <p className="text-center py-8 text-muted-foreground">No campaigns to show yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left pb-3 font-medium">Campaign</th>
                        <th className="text-left pb-3 font-medium">Segment</th>
                        <th className="text-center pb-3 font-medium">Status</th>
                        <th className="text-right pb-3 font-medium">Sent</th>
                        <th className="text-right pb-3 font-medium">Opens</th>
                        <th className="text-right pb-3 font-medium">Clicks</th>
                        <th className="text-right pb-3 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recent_campaigns.map((camp) => (
                        <tr key={camp.campaign_id} className="border-b last:border-0">
                          <td className="py-3">
                            <p className="font-medium">{camp.name}</p>
                            <p className="text-xs text-muted-foreground">{camp.subject}</p>
                          </td>
                          <td className="py-3 capitalize">{camp.segment?.replace(/_/g, ' ')}</td>
                          <td className="py-3 text-center">
                            <Badge variant={camp.status === 'sent' ? 'default' : 'secondary'} className="text-xs">
                              {camp.status}
                            </Badge>
                          </td>
                          <td className="py-3 text-right font-medium">{camp.sent_count || 0}</td>
                          <td className="py-3 text-right">{camp.open_count || 0}</td>
                          <td className="py-3 text-right">{camp.click_count || 0}</td>
                          <td className="py-3 text-right text-muted-foreground text-xs">
                            {new Date(camp.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminEmailMarketing;
